/**
 * 16回以上施術理由一覧 - Long Term Frequent List Page
 * Layout: Full-width table (same pattern as ConsentListPage)
 */

import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import {
  AppButton,
  AppTable,
  AppSpin,
  AppTooltip,
  AppPagination,
  AppTableActionButton,
} from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import type { ColumnsType } from 'antd/es/table';
import type { LongTermFrequentItem } from '@/services/longTermFrequentService';
import {
  useLongTermFrequentList,
  type LongTermFrequentListFilters,
} from '@/hooks/main/useLongTermFrequentList';
import { useLongTermFrequentDelete } from '@/hooks/main/useLongTermFrequentEdit';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { getAntdTableSortOrder } from '@/utils';
import { convGtJMonth, yearmonthToDateStr } from '@/utils/dateConverter';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { getApiErrorMessage } from '@/utils/apiError';
import { NotFound } from '@/pages/main/system/NotFound';
import { TENANT_ROUTES } from '@/constants/routes';
import { ADMIN_LIST_DEFAULT_PER_PAGE } from '@/constants/admin';
import {
  DELETE_COMPLETED_MESSAGE,
  DELETE_CONFIRM_DATA_MESSAGE,
  DELETE_ERROR_MESSAGE,
} from '@/constants/messages';
import {
  STANDARD_DELETE_CONFIRM_TITLE,
  STANDARD_DELETE_CONFIRM_OK_TEXT,
  STANDARD_DELETE_CONFIRM_CANCEL_TEXT,
} from '@/constants/deleteConfirm';

export const LongTermFrequentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant, id: patientId } = useParams<{ tenant?: string; id?: string }>();
  const toast = useAppToast();
  const { confirm } = useAppModal();
  const { serviceType } = useTheme();
  const queryClient = useQueryClient();

  const [sortState, setSortState] = useState<{
    field: string | undefined;
    order: 'asc' | 'desc' | undefined;
    serviceType: string | undefined;
  }>({ field: undefined, order: undefined, serviceType });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (sortState.serviceType !== serviceType) {
      queryClient.removeQueries({ queryKey: ['long-term-frequents', 'list'] });
      setSortState({ field: undefined, order: undefined, serviceType });
      setCurrentPage(1);
    }
  }, [queryClient, serviceType, sortState.serviceType]);

  const sortField = sortState.serviceType === serviceType ? sortState.field : undefined;
  const sortOrder = sortState.serviceType === serviceType ? sortState.order : undefined;

  const filters = useMemo<LongTermFrequentListFilters>(
    () => ({
      page: currentPage,
      per_page: ADMIN_LIST_DEFAULT_PER_PAGE,
      sort_type: sortField,
      sort_dir: sortOrder,
    }),
    [currentPage, sortField, sortOrder]
  );

  const {
    data: listResponse,
    isLoading,
    isFetching,
    isError,
  } = useLongTermFrequentList(patientId, filters);
  const deleteMutation = useLongTermFrequentDelete(patientId);
  const isBusy = isLoading || isFetching || deleteMutation.isPending;

  const listData = listResponse?.data;
  const patient = listData?.patient;
  const list = listData?.long_term_frequents ?? [];
  const pagination = listData?.pagination;
  const totalCount = pagination?.total ?? listData?.total_count ?? list.length;

  const handleSortChange = useCallback(
    (_pagination: any, _filters: any, sorter: any) => {
      if (isFetching) return;
      const sort = Array.isArray(sorter) ? sorter[0] : sorter;
      if (!sort?.columnKey || !sort?.order) {
        setSortState({ field: undefined, order: undefined, serviceType });
      } else {
        setSortState({
          field: String(sort.columnKey),
          order: sort.order === 'descend' ? 'desc' : 'asc',
          serviceType,
        });
      }
      setCurrentPage(1);
    },
    [isFetching, serviceType]
  );

  const handleDeleteConfirm = useCallback(
    async (record: LongTermFrequentItem) => {
      try {
        await deleteMutation.mutateAsync(record.id);
        toast.success(DELETE_COMPLETED_MESSAGE);
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, DELETE_ERROR_MESSAGE));
      }
    },
    [deleteMutation, toast]
  );

  const handleEdit = useCallback(
    (record: LongTermFrequentItem) => {
      navigate(TENANT_ROUTES.LONG_TERM_FREQUENTS.EDIT(tenant || '', patientId || '', record.id));
    },
    [navigate, tenant, patientId]
  );

  const columns = useMemo<ColumnsType<LongTermFrequentItem>>(() => {
    const formatYearMonth = (yearMonth: string | number) => {
      const ym = Number(String(yearMonth).padStart(6, '0'));
      return convGtJMonth(yearmonthToDateStr(ym));
    };

    return [
      {
        title: '施術年月',
        dataIndex: 'visit_yearmonth',
        key: 'visit_yearmonth',
        width: 140,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'visit_yearmonth', sortOrder as any),
        render: (yearMonth: number) => (yearMonth ? formatYearMonth(yearMonth) : ''),
      },
      {
        title: '施術回数',
        dataIndex: 'visit_count',
        key: 'visit_count',
        width: 120,
        align: 'left',
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'visit_count', sortOrder as any),
        render: (count: number) => (count ? String(count) : ''),
      },
      {
        title: '施術師名',
        dataIndex: 'massager_name',
        key: 'massager_name',
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'massager_name', sortOrder as any),
        render: (_: unknown, record: LongTermFrequentItem) => (
          <AppTooltip title={record.massager_name || ''} showOnlyOnOverflow>
            <div className="line-clamp-2">{record.massager_name || ''}</div>
          </AppTooltip>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 170,
        align: 'center' as const,
        render: (_, record: LongTermFrequentItem) => (
          <div className="flex justify-center gap-1">
            <AppTableActionButton
              action="edit"
              disabled={isBusy}
              onClick={e => {
                e.stopPropagation();
                handleEdit(record);
              }}
            >
              編集
            </AppTableActionButton>
            <AppTableActionButton
              action="delete"
              disabled={isBusy}
              icon={<i className="fa fa-trash-o" aria-hidden="true" />}
              onClick={e => {
                e.stopPropagation();
                confirm({
                  title: STANDARD_DELETE_CONFIRM_TITLE,
                  content: DELETE_CONFIRM_DATA_MESSAGE,
                  okButtonProps: { danger: true },
                  okText: STANDARD_DELETE_CONFIRM_OK_TEXT,
                  cancelText: STANDARD_DELETE_CONFIRM_CANCEL_TEXT,
                  onOk: () => handleDeleteConfirm(record),
                });
              }}
            >
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ];
  }, [sortField, sortOrder, confirm, handleDeleteConfirm, handleEdit, isBusy]);

  if (isError && !listData) {
    return <NotFound />;
  }

  return (
    <div className="relative">
      {isFetching && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <AppSpin size="large" />
        </div>
      )}

      <CollapsibleCard title="16回一覧">
        <PatientPageHeader
          patient={patient}
          patientId={patientId || ''}
          tenant={tenant || ''}
          navigate={navigate}
          activeKey="long-term-frequents"
          disabled={isBusy}
          actions={
            <AppButton
              type="primary"
              disabled={isBusy}
              icon={<i className="fa fa-file-text-o" />}
              onClick={() =>
                navigate(TENANT_ROUTES.LONG_TERM_FREQUENTS.CREATE(tenant || '', patientId || ''))
              }
            >
              新規作成
            </AppButton>
          }
        />

        <AppTable<LongTermFrequentItem>
          rowKey={r => String(r.id)}
          columns={columns}
          dataSource={list}
          pagination={false}
          bordered
          recordCount={totalCount}
          onChange={handleSortChange}
          customLabelTotal="表示件数"
        />

        {pagination && pagination.last_page > 1 && (
          <div className="flex justify-center mt-4">
            <AppPagination
              current={pagination.current_page}
              total={pagination.total}
              pageSize={pagination.per_page}
              onChange={setCurrentPage}
              disabled={isBusy}
            />
          </div>
        )}
      </CollapsibleCard>
    </div>
  );
};

export default LongTermFrequentListPage;
